import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';

import './order-list-search.css';
import { Link, useHistory } from 'react-router-dom';
import {
    EyeOutlined,
    FilePptOutlined,
    FileUnknownOutlined,
    FilterOutlined,
    PhoneOutlined,
    SearchOutlined,
    PlusOutlined,
    FilePdfOutlined,
    SendOutlined,
    EllipsisOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { Button, Divider, Dropdown, Empty, Menu, Select, Spin, notification, Tag, Card } from 'antd';
import { observer } from 'mobx-react';
import StatusDropDown from 'components/SalesOrders/DropDowns/StatusDropDown';
import NewOrder from 'components/SalesOrders/NewOrder';
import api from 'utils/api';
import UIStore from '@/stores/UIStore';
import DateRange from 'components/SalesOrders/DropDowns/DateRange';
import UserStore from 'stores/UserStore';
import Verify from 'components/Verify';
import { ColumnProps, TableProps, TablePaginationConfig } from 'antd/es/table';
import { SalesOrderProps } from 'types/types';

import ContactRepForm from 'components/ContactRepForm';
import RequestInvoice from 'components/ContactActions/RequestInvoice/';
import * as _ from 'lodash';
import { Price, ErrorRender } from '@partsbadger/library';

import { OrderPreview } from '../../../components/SalesOrders';
import dayjs from 'dayjs';
import { CustomResponsiveTable } from 'components/common/SimpleResponsiveTable';
import { ListPageHeader, StatusChip } from 'components/ListPageHeader';
import { buildOrderListParams, normalizeDateRange, OrderListParams, shouldSkipOrderRowNavigation } from './helpers';

import SalesOrderStore from 'stores/SalesOrderStore';

const ShippingAdjustments = lazy(() => import('components/SalesOrders/ShippingAdjustments'));

const RequestBatchAdjustment = lazy(() => import('components/SalesOrders/RequestBatchAdjustment'));

const STATUS_CHIPS: StatusChip[] = [
    { label: 'All', value: undefined },
    { label: 'In Review', value: 'In Review' },
    { label: 'Order in Production', value: 'Order in Production' },
    { label: 'Order Shipped in Full', value: 'Order Shipped in Full' },
    { label: 'Order Shipped and Paid', value: 'Order Shipped and Paid' },
    { label: 'Order Cancelled', value: 'Order Cancelled' },
];

interface ISalesOrderProps {
    id: number;
    name: string;
    product_description: string;
    quantity: number;
    unit_price: number;
    list_price: number;
    discount: number;
    total_after_discount: number;
    taxes: number;
    total_price: number;
    thumbnail: 'http://example.com';
    sales_order: SalesOrderProps;
    PO?: {
        id: number;
        name: string;
        purchase_order: string;
        customer_status: string;
        required_ship_date: '2019-08-24';
    };
}

type SearchDropdownPORow = {
    id: number;
    thumbnail: ISalesOrderProps['thumbnail'];
    PO: SalesOrderProps;
};

type SearchDropdownRow = ISalesOrderProps | SearchDropdownPORow;

function isSearchLineItem(row: SearchDropdownRow): row is ISalesOrderProps {
    return 'sales_order' in row;
}

function checkStatusValues(status: string): boolean {
    if (status.includes('Shipped in Partial') || status.includes('Shipped in Full')) {
        return false;
    }
    return true;
}

function buildSearchOptionsFromResults(results: ISalesOrderProps[]): SearchDropdownRow[] {
    const saleOrderIds = results.map(
        (row: ISalesOrderProps) => row.sales_order.purchase_order !== null && row.sales_order.id
    );

    const poRows = results
        .filter((item: ISalesOrderProps, index: number) => {
            return item.sales_order.purchase_order !== null && !saleOrderIds.includes(item.sales_order.id, index + 1);
        })
        .map(
            (po_item: ISalesOrderProps): SearchDropdownPORow => ({
                PO: po_item.sales_order,
                id: po_item.id,
                thumbnail: po_item.thumbnail,
            })
        );

    return [...results, ...poRows];
}

const OrderListContent: React.FC = observer(() => {
    const history = useHistory();
    const [data, setData] = useState<SalesOrderProps[]>([]);
    const [status, setStatus] = useState<string>();
    const [selected, setSelected] = useState<string>();
    const [searchSelectValue, setSearchSelectValue] = useState<string | number | undefined>(undefined);
    const [selectedOrder, setSelectedOrder] = useState<SalesOrderProps>();
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [searchData, setSearchData] = useState<SearchDropdownRow[]>([]);
    const [createdDates, setCreatedDates] = useState<[string, string]>();
    const [dates, setDates] = useState<[string, string]>();
    const [showOrder, setShowOrder] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
    });
    const [tableLoading, setTableLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [show_contact_to_rep, setShowContactRep] = useState(false);
    const [emailSubject, setEmailSubject] = useState<string>();
    const [isDropDownMenuOpen, setIsDropDownMenuOpen] = useState(false);
    const [preview, setPreview] = useState<boolean>(false);
    const [showShippingAdjustments, setShowShippingAdjustments] = useState<boolean>(false);
    const [showRequestBatchAdjustment, setShowRequestBatchAdjustment] = useState<boolean>(false);
    const [invoiceRecord, setInvoiceRecord] = useState<SalesOrderProps | null>(null);

    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;

    const { Option } = Select;

    const hasAccount = Boolean(UserStore?.user?.default_contact?.account);

    const loadOrders = useCallback(
        async (
            newPagination?: TablePaginationConfig,
            _filters?: unknown,
            sorter?: { field?: string; order?: string }
        ) => {
            setTableLoading(true);
            const prev = paginationRef.current;
            const effective = {
                current: newPagination?.current ?? prev.current ?? 1,
                pageSize: newPagination?.pageSize ?? prev.pageSize ?? 10,
            };

            const params = buildOrderListParams({
                search: selected,
                page: effective.current,
                pageSize: effective.pageSize,
                status,
                createdDates,
                shipDates: dates,
                sorter,
            });

            try {
                const response = await api.Orders.all(params);
                setData(response.results);
                setPagination(prevState => ({
                    ...prevState,
                    current: effective.current,
                    pageSize: effective.pageSize,
                    total: response.count,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                }));
            } catch (error) {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error as never} />,
                    placement: 'topRight',
                });
            } finally {
                setTableLoading(false);
            }
        },
        [selected, status, createdDates, dates]
    );

    useEffect(() => {
        UIStore.setSelectedKey(['orders']);
        UIStore.setDocumentTitle('Orders');

        if (!UserStore.isVerified) return;

        const pageSize = paginationRef.current.pageSize ?? 10;
        setPagination(p => ({ ...p, current: 1 }));
        void loadOrders({ current: 1, pageSize }, undefined, undefined);
    }, [selected, status, createdDates, dates, UserStore.isVerified, loadOrders]);

    const onSearch = useCallback(async (value: string) => {
        const params: OrderListParams = {
            search: value,
            page: 1,
        };

        setSearchLoading(true);

        try {
            const response = await api.Orders.searchBy(params);
            setPagination(prev => ({ ...prev, current: 1, total: response.count }));
            setSearchData(buildSearchOptionsFromResults(response.results));
        } catch (error) {
            notification.error({
                duration: 10,
                message: 'Error',
                description: <ErrorRender error={error as never} />,
                placement: 'bottomRight',
            });
        } finally {
            setSearchLoading(false);
        }
    }, []);

    const debouncedSearchRef = useRef(
        _.debounce((search: string) => {
            void onSearch(search);
        }, 400)
    );

    useEffect(() => {
        debouncedSearchRef.current = _.debounce((search: string) => {
            void onSearch(search);
        }, 400);
    }, [onSearch]);

    useEffect(() => {
        return () => debouncedSearchRef.current.cancel();
    }, []);

    const handleTableChange: TableProps<SalesOrderProps>['onChange'] = (newPagination, _filters, sorter) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        setPagination(prev => ({
            ...prev,
            current: newPagination?.current ?? prev.current,
            pageSize: newPagination?.pageSize ?? prev.pageSize,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
        }));
        void loadOrders(
            newPagination ?? undefined,
            _filters,
            s && typeof s === 'object' && 'field' in s
                ? { field: s.field as string, order: s.order as string }
                : undefined
        );
    };

    const emptyTable = () => {
        if (status || selected || createdDates || dates) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span>Could not find anything with your search</span>}
                />
            );
        }
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <span style={{ color: '#000' }}>
                        Place an order with PartsBadger and see the status of your orders here!
                    </span>
                }
            >
                <Button style={{ textAlign: 'center' }} type="primary">
                    <Link to="/my-account/quotes/new-quote">Submit an RFQ</Link>
                </Button>
            </Empty>
        );
    };

    const onChangeSelect = (value: number | string | undefined) => {
        setSearchSelectValue(value);

        if (value === undefined || value === null) {
            setSelected(undefined);
            return;
        }

        if (typeof value === 'string' && value.includes('PO:')) {
            const poNumber = value.split(':')[1];
            const match = searchData.find(item => item.PO && item.PO.purchase_order === poNumber);
            if (match?.PO) {
                setSelected(match.PO.name);
            }
            return;
        }

        if (typeof value === 'number') {
            const match = searchData.find(
                (item): item is ISalesOrderProps => isSearchLineItem(item) && !item.PO && item.id === value
            );
            if (match) {
                setSelected(match.sales_order.name);
            }
        }
    };

    const columns: ColumnProps<SalesOrderProps>[] = useMemo(
        () => [
            {
                title: 'Order Number',
                dataIndex: 'name',
                width: 105,
                filterIcon: <SearchOutlined style={{ color: selected ? '#c00' : '#bfbfbf' }} />,
            },
            {
                title: 'PO Number',
                dataIndex: 'purchase_order',
                width: 105,
                filterIcon: <SearchOutlined style={{ color: selected && searchSelectValue ? '#c00' : '#bfbfbf' }} />,
            },
            {
                title: 'Date',
                dataIndex: 'created_time',
                width: 105,
                filteredValue: createdDates ?? null,
                filterDropdown: (
                    <DateRange
                        current={createdDates}
                        search={(min, max) => {
                            setCreatedDates(normalizeDateRange(min, max));
                        }}
                    />
                ),
                filterIcon: <FilterOutlined />,
                render: (_text, record) => {
                    return (
                        <div>
                            <div>{dayjs(record.created_time).format('YYYY-MM-DD')}</div>
                            <div className={'italic'}>By {record.contact?.fullname}</div>
                        </div>
                    );
                },
            },
            {
                title: 'Status',
                dataIndex: 'customer_status',
                width: 105,
                filterDropdown: <StatusDropDown current={status} status={setStatus} />,
                filterIcon: <FilterOutlined />,
                render: (text, record) => {
                    return (
                        <div>
                            {record.create_3d_files && record.status === 'Waiting on 3d File' ? (
                                <Tag color="orange">Need 3D approval</Tag>
                            ) : (
                                <span>{text ?? ' - '}</span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: 'Estimated Ship Date',
                dataIndex: 'required_ship_date',
                width: 150,
                filteredValue: dates ?? null,
                filterDropdown: (
                    <DateRange
                        current={dates}
                        search={(min, max) => {
                            setDates(normalizeDateRange(min, max));
                        }}
                    />
                ),
                filterIcon: <FilterOutlined />,
                sorter: true,
                render: text => <span>{text ? text.substring(0, 10) : ' --- '}</span>,
            },
            {
                title: 'Total',
                dataIndex: 'grand_total',
                width: 100,
                render: (_text, record) => <Price value={record.grand_total} />,
            },
            {
                title: 'Actions',
                dataIndex: 'actions',
                width: 160,
                align: 'right',
                render: (_text, record) => {
                    const actionsMenu = (
                        <Menu>
                            <Menu.Item
                                key="contact"
                                icon={<PhoneOutlined />}
                                onClick={() => {
                                    setSelectedOrder(record);
                                    setEmailSubject(record.name);
                                    setShowContactRep(true);
                                }}
                            >
                                Contact support
                            </Menu.Item>
                            {hasAccount && (
                                <Menu.Item
                                    key="invoice"
                                    icon={<FileTextOutlined />}
                                    disabled={checkStatusValues(record.status)}
                                    onClick={() => setInvoiceRecord(record)}
                                >
                                    Request Invoice
                                </Menu.Item>
                            )}
                            {record.shipping_requirements?.includes('Batch/Blanket Releases') && (
                                <Menu.Item
                                    key="batch"
                                    onClick={() => {
                                        setShowRequestBatchAdjustment(true);
                                        SalesOrderStore.setOrder(record);
                                    }}
                                >
                                    Request Batch Adjustment
                                </Menu.Item>
                            )}
                            {record?.customer_status !== 'In Review' && record?.customer_status !== 'Cancelled' && (
                                <Menu.Item
                                    key="pdf"
                                    icon={<FilePdfOutlined />}
                                    onClick={() => {
                                        setPreview(true);
                                        SalesOrderStore.setOrder(record);
                                    }}
                                >
                                    Sales Order Confirmation
                                </Menu.Item>
                            )}
                            <Menu.Item
                                key="shipping"
                                icon={<SendOutlined />}
                                onClick={() => {
                                    setShowShippingAdjustments(true);
                                    SalesOrderStore.setOrder(record);
                                }}
                            >
                                Shipping Adjustments
                            </Menu.Item>
                        </Menu>
                    );

                    return (
                        <div className="flex items-center justify-end gap-1">
                            <Button
                                size="small"
                                type="link"
                                icon={<EyeOutlined />}
                                href={'/my-account/orders/' + record.id}
                                data-testid={`order-action-view-${record.id}`}
                                style={{ display: 'inline-flex', alignItems: 'center' }}
                            >
                                View
                            </Button>
                            <Dropdown overlay={actionsMenu} trigger={['click']}>
                                <Button
                                    size="small"
                                    type="default"
                                    icon={<EllipsisOutlined />}
                                    data-testid={`order-action-more-${record.id}`}
                                />
                            </Dropdown>
                        </div>
                    );
                },
            },
        ],
        [status, createdDates, dates, selected, searchSelectValue, hasAccount]
    );

    return (
        <>
            <ListPageHeader
                title="Orders"
                subtitle="Manage and track all your orders"
                action={
                    <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={() => setShowOrder(true)}
                        className="w-full sm:w-auto shrink-0"
                    >
                        New Order
                    </Button>
                }
                searchNode={
                    <Select
                        allowClear
                        showSearch
                        value={searchSelectValue}
                        optionLabelProp="label"
                        suffixIcon={null}
                        clearIcon={searchLoading ? <Spin size="small" /> : undefined}
                        placeholder="Search orders, PO, or product…"
                        onChange={onChangeSelect}
                        onSearch={(search: string) => {
                            setIsTyping(true);
                            debouncedSearchRef.current(search);
                        }}
                        onDropdownVisibleChange={value => setIsDropDownMenuOpen(value)}
                        size="large"
                        notFoundContent={
                            searchLoading ? (
                                <Spin size="small" />
                            ) : isTyping ? (
                                <div>No results found</div>
                            ) : (
                                <div>Enter the Order or PO number, the product name…</div>
                            )
                        }
                        filterOption={false}
                        loading={searchLoading}
                        className="w-full order-list-search-select"
                    >
                        {searchData.map(item => {
                            if (item.PO) {
                                return (
                                    <Option
                                        key={item.id}
                                        value={`PO:${item.PO.purchase_order}`}
                                        label={`PO: ${item.PO.purchase_order} - ${item.PO.name}`}
                                        style={{ fontSize: 13 }}
                                    >
                                        <div style={{ display: 'flex' }}>
                                            {item.thumbnail ? (
                                                <img
                                                    style={{
                                                        width: '40px',
                                                        height: '38px',
                                                        marginRight: 5,
                                                        borderRadius: '2px',
                                                    }}
                                                    src={item.thumbnail}
                                                    alt=""
                                                />
                                            ) : (
                                                <FilePptOutlined
                                                    style={{ fontSize: 30, marginTop: 5, marginRight: 5 }}
                                                />
                                            )}

                                            <Divider type="vertical" style={{ fontSize: 45 }} />

                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <div>
                                                    <span style={{ fontWeight: 'bold', paddingRight: 2 }}>PO:</span>
                                                    <span>{item.PO.purchase_order}</span>
                                                </div>
                                                <div hidden={!isDropDownMenuOpen}>
                                                    <span style={{ fontWeight: 'bold', paddingRight: 2 }}>Order:</span>
                                                    <span>{item.PO.name}</span>
                                                </div>
                                                <div hidden={!isDropDownMenuOpen}>
                                                    <span style={{ fontWeight: 'bold', paddingRight: 2 }}>Status:</span>
                                                    <span>{item.PO.customer_status}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Option>
                                );
                            }
                            if (!isSearchLineItem(item)) {
                                return null;
                            }
                            return (
                                <Option
                                    key={item.id}
                                    value={item.id}
                                    label={`Part: ${item.name}`}
                                    style={{ fontSize: 13 }}
                                >
                                    <div style={{ display: 'flex' }}>
                                        {item.thumbnail ? (
                                            <img
                                                style={{ width: '40px', height: '38px', marginRight: 5 }}
                                                src={item.thumbnail}
                                                alt=""
                                            />
                                        ) : (
                                            <FileUnknownOutlined
                                                style={{ fontSize: 30, marginTop: 5, marginRight: 5 }}
                                            />
                                        )}

                                        <Divider type="vertical" style={{ fontSize: 45 }} />

                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div>
                                                <span style={{ fontWeight: 'bold', paddingRight: 2 }}>Part:</span>
                                                <span>{item.name}</span>
                                            </div>
                                            <div hidden={!isDropDownMenuOpen}>
                                                <span style={{ fontWeight: 'bold', paddingRight: 2 }}>On Orders:</span>
                                                <span>{item.sales_order.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Option>
                            );
                        })}
                    </Select>
                }
                chips={STATUS_CHIPS}
                activeChip={status}
                onChipChange={setStatus}
                dates={dates}
                onDatesChange={setDates}
                showDateFilter={false}
                totalCount={pagination.total ?? 0}
                entityName="orders"
                hasActiveFilters={!!(status || selected || createdDates || dates)}
                onClearFilters={() => {
                    setStatus(undefined);
                    setCreatedDates(undefined);
                    setDates(undefined);
                    setSelected(undefined);
                    setSearchSelectValue(undefined);
                }}
            >
                {showOrder && <NewOrder onClose={() => setShowOrder(false)} />}
            </ListPageHeader>

            <div className="container" data-testid="customer-orders-page">
                <Card>
                    <CustomResponsiveTable
                        columns={columns}
                        loading={tableLoading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        onRow={record => ({
                            onClick: event => {
                                const target = event.target as HTMLElement | null;
                                if (shouldSkipOrderRowNavigation(target)) {
                                    return;
                                }

                                history.push(`/my-account/orders/${record.id}`);
                            },
                            style: { cursor: 'pointer' },
                        })}
                        emptyTable={emptyTable}
                        data={data}
                        scroll={typeof window !== 'undefined' && window.innerWidth <= 1160 ? { x: 1160 } : { x: true }}
                        tableLayout="fixed"
                    />
                </Card>
                {show_contact_to_rep && UserStore.user && (
                    <ContactRepForm
                        emailSubject={emailSubject}
                        current_user={UserStore.user}
                        handleClose={() => setShowContactRep(false)}
                        order={selectedOrder}
                    />
                )}
                {invoiceRecord && (
                    <RequestInvoice
                        open={!!invoiceRecord}
                        onClose={() => setInvoiceRecord(null)}
                        salesorder={invoiceRecord}
                    />
                )}
                {preview && SalesOrderStore?.order && (
                    <div>
                        <OrderPreview sales_order_id={SalesOrderStore?.order?.id} onClose={() => setPreview(false)} />
                    </div>
                )}

                {showShippingAdjustments && (
                    <Suspense fallback={<Spin />}>
                        <ShippingAdjustments
                            showShippingAdjustments={showShippingAdjustments}
                            onClose={() => {
                                setShowShippingAdjustments(false);
                            }}
                        />
                    </Suspense>
                )}

                <Suspense fallback={<Spin />}>
                    <RequestBatchAdjustment
                        showRequestBatchAdjustment={showRequestBatchAdjustment}
                        onClose={() => {
                            setShowRequestBatchAdjustment(false);
                        }}
                    />
                </Suspense>
            </div>
        </>
    );
});

const OrderList: React.FC = observer(() => {
    if (!UserStore.isVerified) {
        return <Verify />;
    }
    return <OrderListContent />;
});

export default OrderList;
